import React, { useState, useContext } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    CardActions,
    Button,
    Typography,
    Grid,
    Container,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    TextField,
    InputAdornment,
    Paper,
    Alert
} from '@mui/material';
import {
    Check as CheckIcon,
    Star as StarIcon,
    Rocket as RocketIcon,
    Business as BusinessIcon,
    LocalOffer as CouponIcon,
    CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { userContext } from '../../stores/users/userContext';
import { messageContext } from '../../stores/messages/messageContext';
import { hostApi } from '../constants';

const plans = [
    {
        id: 'Starter',
        name: 'Starter',
        price: 20,
        currency: 'USD',
        interval: 'month',
        description: 'Perfect for getting started',
        features: [
            '5 GB of transcribed videos',
            'Files kept for while subscription is active',
            'Community support via Discord',
            'MP4, MP3, GIF video export formats',
            'Standard transcription accuracy'
        ],
        buttonText: 'Get Started Now',
        buttonVariant: 'outlined',
        popular: false,
        icon: <CheckIcon />
    },
    {
        id: 'Pro',
        name: 'Premium',
        price: 35,
        currency: 'USD',
        interval: 'month',
        description: 'Most popular for hobbyists and professionals',
        features: [
            '10 GB of transcribed videos',
            'File keep while subscription is active',
            'Priority email support',
            'MP4, MP3, GIF, SRT, VTT, and more video export formats',
            'High-accuracy AI transcription',
            'Export to multiple formats',
        ],
        limitations: [],
        buttonText: 'Start Premium',
        buttonVariant: 'contained',
        popular: true,
        icon: <StarIcon />,
    },
    {
        id: 'Enterprise',
        name: 'Enterprise',
        price: 99,
        currency: 'USD',
        interval: 'month',
        description: 'For professionals and businesses',
        features: [
            'Unlimited GB of transcribed videos',
            'File keep while subscription is active',
            'Priority email support',
            'MP4, MP3, GIF, SRT, VTT, and more video export formats',
            'High-accuracy AI transcription',
            'Export to multiple formats',
        ],
        limitations: [],
        buttonText: 'Start Enterprise',
        buttonVariant: 'outlined',
        popular: false,
        icon: <BusinessIcon />,
        isContactSales: false
    }
];

const SubscriptionPlans = () => {
    const [loading, setLoading] = useState(null);
    const userStore = useContext(userContext);
    const messageStore = useContext(messageContext);
    const [buttonEnabled, setButtonEnabled] = useState(false);

    // Coupon-related state
    const [couponCode, setCouponCode] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState('');

    // Helper function to calculate discounted price
    const calculateDiscountedPrice = (originalPrice, coupon) => {
        if (!coupon || originalPrice === 0) return originalPrice;

        if (coupon.type === 'percentage') {
            return originalPrice * (1 - coupon.value / 100);
        } else if (coupon.type === 'fixed') {
            return Math.max(0, originalPrice - coupon.value);
        }
        return originalPrice;
    };

    // Function to validate and apply coupon
    const applyCoupon = async () => {
        if (!couponCode.trim()) {
            setCouponError('Please enter a coupon code');
            return;
        }

        setCouponLoading(true);
        setCouponError('');

        try {
            const formData = new FormData();
            formData.append('coupon_code', couponCode.trim().toUpperCase());

            const response = await fetch(`${hostApi}validate-coupon/`, {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Invalid coupon code');
            }

            setAppliedCoupon({
                code: couponCode.trim().toUpperCase(),
                type: result.type, // 'percentage' or 'fixed'
                value: result.value, // percentage (e.g., 20) or fixed amount (e.g., 5)
                description: result.description || `${result.value}${result.type === 'percentage' ? '%' : '$'} off`
            });

            messageStore.setMessage('Coupon applied successfully!', 'success');
        } catch (err) {
            console.error('Coupon validation error:', err);
            setCouponError(err.message);
        } finally {
            setCouponLoading(false);
        }
    };

    // Function to remove applied coupon
    const removeCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponError('');
    };

    const handleSubscribe = async (plan) => {
        setButtonEnabled(true);
        if (plan.isContactSales) {
            window.location.href = '/contact/';
            return;
        }
        // handle all other plans
        if (!userStore.isUserAuth()) {
            messageStore.setMessage('Please login/signup to subscribe', 'error');
            setTimeout(() => {
                window.location.href = '/signup/';
            }, 2000);
            return;
        }
        if (userStore.isUserAuth() && userStore.isUserStripeActive()) {
            messageStore.setMessage('You are already subscribed', 'error');
            setTimeout(() => {
                window.location.href = '/downloaded/';
            }, 2000);
            return;
        }

        setLoading(plan.id);

        try {
            // Create checkout session via Django backend
            const formData = new FormData();
            formData.append('token', userStore.getToken());
            formData.append('plan', plan.id);

            // Include coupon code if applied
            if (appliedCoupon) {
                formData.append('coupon_code', appliedCoupon.code);
            }

            const response = await fetch(`${hostApi}create-checkout-session/`, {
                method: 'POST',
                body: formData,
            });

            const session = await response.json();

            if (!response.ok) {
                throw new Error(session.error || 'Failed to create checkout session');
            }

            // Redirect to Stripe Checkout using the session URL
            if (session.url) {
                window.location.href = session.url;
            } else {
                throw new Error('No checkout URL provided by server');
            }

        } catch (err) {
            console.error('Subscription error:', err);
            messageStore.setMessage(err.message, 'error');
        } finally {
            setLoading(null);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
            <Box textAlign="center" mb={6}>
                <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
                    Choose Your Plan
                </Typography>
                <Typography variant="h5" color="text.secondary" mb={4}>
                    Unlock the power of AI-driven video transcription and editing
                </Typography>

                {/* Coupon Section */}
                <Box maxWidth="sm" mx="auto" mb={4}>
                    <Paper elevation={1} sx={{ p: 3, backgroundColor: 'background.default' }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <CouponIcon color="primary" />
                            <Typography variant="h6" fontWeight="bold">
                                Have a discount code?
                            </Typography>
                        </Box>

                        {!appliedCoupon ? (
                            <Box display="flex" gap={2} alignItems="flex-start">
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Enter coupon code"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    error={!!couponError}
                                    helperText={couponError}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CouponIcon color="action" fontSize="small" />
                                            </InputAdornment>
                                        )
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            applyCoupon();
                                        }
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={applyCoupon}
                                    disabled={couponLoading || !couponCode.trim()}
                                    startIcon={couponLoading ? <CircularProgress size={16} /> : null}
                                    sx={{ minWidth: 100 }}
                                >
                                    {couponLoading ? 'Checking...' : 'Apply'}
                                </Button>
                            </Box>
                        ) : (
                            <Alert
                                severity="success"
                                action={
                                    <Button size="small" onClick={removeCoupon}>
                                        Remove
                                    </Button>
                                }
                                icon={<SuccessIcon />}
                            >
                                <strong>{appliedCoupon.code}</strong> - {appliedCoupon.description}
                            </Alert>
                        )}
                    </Paper>
                </Box>
            </Box>

            <Grid container spacing={4} alignItems="flex-end">
                {plans.map((plan) => {
                    const originalPrice = plan.price;
                    const discountedPrice = calculateDiscountedPrice(originalPrice, appliedCoupon);
                    const hasDiscount = appliedCoupon && originalPrice > 0 && discountedPrice < originalPrice;

                    return (
                        <Grid item xs={12} md={4} key={plan.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    border: plan.popular ? '2px solid' : '1px solid',
                                    borderColor: plan.popular ? 'primary.main' : 'divider',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        transition: 'transform 0.2s ease-in-out',
                                        boxShadow: 4
                                    }
                                }}
                            >
                                {plan.popular && (
                                    <Chip
                                        label="Most Popular"
                                        color="primary"
                                        sx={{
                                            position: 'absolute',
                                            top: -12,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                )}

                                {/* Discount badge for non-free plans */}
                                {hasDiscount && (
                                    <Chip
                                        label={`Save $${(originalPrice - discountedPrice).toFixed(2)}`}
                                        color="error"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: plan.popular ? 120 : 8,
                                            right: 16,
                                            fontWeight: 'bold',
                                            fontSize: '0.75rem',
                                            zIndex: 1
                                        }}
                                    />
                                )}

                                <CardHeader
                                    title={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {plan.icon}
                                            <Typography variant="h5" fontWeight="bold">
                                                {plan.name}
                                            </Typography>
                                        </Box>
                                    }
                                    subheader={plan.description}
                                    titleTypographyProps={{ align: 'center' }}
                                    subheaderTypographyProps={{ align: 'center' }}
                                    sx={{
                                        backgroundColor: plan.popular
                                            ? 'primary.main'
                                            : 'background.default',
                                        color: plan.popular ? 'primary.contrastText' : 'inherit',
                                        '& .MuiCardHeader-subheader': {
                                            color: plan.popular ? 'primary.contrastText' : 'text.secondary'
                                        }
                                    }}
                                />

                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box textAlign="center" mb={3}>
                                        {hasDiscount ? (
                                            <Box>
                                                <Typography
                                                    variant="h5"
                                                    color="text.secondary"
                                                    sx={{ textDecoration: 'line-through' }}
                                                >
                                                    ${originalPrice}
                                                </Typography>
                                                <Typography variant="h3" fontWeight="bold" color="primary">
                                                    ${discountedPrice.toFixed(2)}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="h3" fontWeight="bold" color="primary">
                                                ${plan.price}
                                            </Typography>
                                        )}
                                        <Typography variant="subtitle1" color="text.secondary">
                                            per {plan.interval}
                                        </Typography>
                                        {plan.trialDays && (
                                            <Typography variant="body2" color="success.main" fontWeight="bold">
                                                {plan.trialDays}-day free trial
                                            </Typography>
                                        )}
                                    </Box>

                                    <List dense>
                                        {plan.features.map((feature, index) => (
                                            <ListItem key={index} sx={{ py: 0.5 }}>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <CheckIcon color="success" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={feature}
                                                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>

                                <CardActions sx={{ p: 3, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant={plan.buttonVariant}
                                        size="large"
                                        onClick={() => handleSubscribe(plan)}
                                        disabled={buttonEnabled}
                                        startIcon={loading === plan.id ? <CircularProgress size={20} /> : null}
                                        sx={{
                                            py: 1.5,
                                            fontWeight: 'bold',
                                            fontSize: '1rem'
                                        }}
                                    >
                                        {loading === plan.id ? 'Processing...' : plan.buttonText}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Box textAlign="center" mt={6}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    All plans include a 30-day money-back guarantee
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Need a custom solution? <Button variant="text" href="/contact/">Contact our sales team</Button>
                </Typography>
            </Box>
        </Container>
    );
};

export default SubscriptionPlans; 